import { Injectable, computed, signal } from '@angular/core';

/**
 * Reference-counted request tracking.
 *
 * Never a boolean: with `isBusy = false` on every completion, two concurrent
 * requests clear the bar as soon as the FIRST one lands, so the UI unlocks
 * while a save is still in flight. Counting is the whole point.
 *
 * Signals, not RxJS - the app is zoneless, so a plain field mutated from an
 * HTTP callback would never repaint.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly total = signal(0);
  private readonly keyed = signal<ReadonlyMap<string, number>>(new Map());

  /** True while any tracked request is outstanding. */
  readonly isBusy = computed(() => this.total() > 0);

  /** Total outstanding requests. Exposed for diagnostics and specs. */
  readonly pending = this.total.asReadonly();

  /**
   * Per-key busy state, for a spinner scoped to one region. Reading this from
   * a template registers a dependency on the keyed map, so it repaints.
   */
  isBusyFor(key: string): boolean {
    return (this.keyed().get(key) ?? 0) > 0;
  }

  /** An empty key tracks the global counter only. */
  start(key = ''): void {
    this.total.update((n) => n + 1);
    if (key) this.bump(key, 1);
  }

  stop(key = ''): void {
    // Clamp at zero: a double-stop must not push the counter negative, which
    // would leave the bar permanently stuck on the next real request.
    this.total.update((n) => (n > 0 ? n - 1 : 0));
    if (key) this.bump(key, -1);
  }

  /** Escape hatch for a route change that abandons in-flight work. */
  reset(): void {
    this.total.set(0);
    this.keyed.set(new Map());
  }

  private bump(key: string, delta: number): void {
    this.keyed.update((current) => {
      const next = new Map(current);
      const value = (next.get(key) ?? 0) + delta;
      if (value > 0) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }
}
